import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { chunk } from 'lodash'
import { clsx } from '@mantine/styles'

import styles from '../VolunteeringFiltersModal.module.scss'
import { FilterProps } from '../VolunteeringFiltersModal.types'

import { Box, Flex, Icon, Typography } from '@percent/lemonade'
import { getUniqueEntriesOnly } from '@percent/workplace-giving/utils/arrayHelpers/arrayHelpers'
import { lowerCaseFirstLetter } from '@percent/workplace-giving/utils/lowerCaseFirstLetter'
import { Chip } from '@percent/workplace-giving/common/components/Chip/Chip'

export function PreferredTimeFilter({ selectedFilters, setSelectedFilters }: FilterProps) {
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()

  const timeFilters = useMemo(() => {
    return [
      {
        label: t('workplace_giving.volunteering.filters.preferredTime.morning'),
        value: '06:00:00-12:00:00'
      },
      {
        label: t('workplace_giving.volunteering.filters.preferredTime.afternoon'),
        value: '12:00:00-17:00:00'
      },
      {
        label: t('workplace_giving.volunteering.filters.preferredTime.evening'),
        value: '17:00:00-21:00:00'
      }
    ]
  }, [t])

  const getActiveFiltersDescription = useMemo(() => {
    const selectedTimeFrames = timeFilters.filter(timeFrame => selectedFilters.timeOfDay?.includes(timeFrame.value))

    if (!selectedTimeFrames.length) {
      return t('workplace_giving.volunteering.filters.preferredTime.allDay')
    }

    const selectedHours = selectedTimeFrames
      .flatMap(({ value }) => {
        const hours = value.split('-')

        return [dayjs(`1970-01-01T${hours[0]}`), dayjs(`1970-01-01T${hours[1]}`)]
      })
      .map(h => h.locale(locale).format('LT'))

    const summary: string[] = chunk(getUniqueEntriesOnly(selectedHours), 2).map((timeFrame, i) => {
      const timeFrameDesc = t('workplace_giving.volunteering.filters.preferredTime.timeWindow', {
        startTime: timeFrame[0],
        endTime: timeFrame[1]
      })

      return i > 0 ? lowerCaseFirstLetter(timeFrameDesc) : timeFrameDesc
    })

    return summary.join(`${' '}${t('workplace_giving.volunteering.filters.preferredTime.timeWindowAnd')}${' '}`)
  }, [locale, selectedFilters.timeOfDay, t, timeFilters])

  return (
    <Box>
      <Typography className={styles.FiltersSectionTitle}>
        {t('workplace_giving.volunteering.filters.preferredTime.label')}
      </Typography>
      <Flex gap="0.5rem">
        {timeFilters.map(filter => {
          const isActive = selectedFilters.timeOfDay?.includes(filter.value)

          return (
            <Chip
              key={filter.label}
              testId={`opportunityTimeFilter-${filter.label}`}
              label={
                <span className={styles.FiltersChipLabel}>
                  {isActive ? <Icon size={4} name="check" color="gray100" /> : null}
                  {filter.label}
                </span>
              }
              classNames={clsx(styles.FiltersChip, { [styles.Active]: isActive })}
              handleClick={() => {
                if (isActive) {
                  const filteredOutTimeOfDayValue = selectedFilters.timeOfDay?.filter(f => f !== filter.value)
                  setSelectedFilters({
                    ...selectedFilters,
                    timeOfDay: filteredOutTimeOfDayValue || []
                  })
                } else {
                  setSelectedFilters({
                    ...selectedFilters,
                    timeOfDay: selectedFilters.timeOfDay ? [...selectedFilters.timeOfDay, filter.value] : [filter.value]
                  })
                }
              }}
            />
          )
        })}
      </Flex>
      <Typography className={styles.FiltersSummary}>{getActiveFiltersDescription}</Typography>
    </Box>
  )
}
